package info.rezident.app.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.MaxWidthStyle
import info.rezident.app.core.AppTracking
import info.rezident.app.core.LocalStorage
import info.rezident.app.core.isValidEmail
import info.rezident.app.data.ContactMessage
import info.rezident.app.data.ContactRepository
import info.rezident.app.seo.*
import info.rezident.app.ui.components.sections.NavHeader
import info.rezident.app.ui.components.widgets.InputField
import info.rezident.app.ui.components.widgets.LargeTextArea
import info.rezident.app.ui.components.widgets.Spacer
import info.rezident.app.ui.components.widgets.inheritPlaceholderColorStyle
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.AutoComplete
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

@Page
@Composable
fun ContactPage() {
    val title = "Contact rezident.info - formular & Instagram"
    val description =
        "Ai întrebări sau propuneri de parteneriat? Scrie-ne prin formular sau pe Instagram. Răspundem în 24-48h."
    val canonicalUrl = "https://rezident.info/contact"
    val ogImageUrl = "https://rezident.info/assets/og/og-image.png"

    Seo(title = title, description = description)

    LaunchedEffect(Unit) {
        document.setCanonicalUrl(canonicalUrl)
        document.setMetaNameTag(name = "robots", content = "index, follow")
        document.setMetaNameTag(name = "twitter:card", content = "summary_large_image")
        document.setMetaNameTag(name = "twitter:title", content = title)
        document.setMetaNameTag(name = "twitter:description", content = description)
        document.setMetaNameTag(name = "twitter:image", content = ogImageUrl)

        document.setOpenGraphTag(property = "og:title", content = title)
        document.setOpenGraphTag(property = "og:description", content = description)
        document.setOpenGraphTag(property = "og:url", content = canonicalUrl)
        document.setOpenGraphTag(property = "og:image", content = ogImageUrl)
    }

    val contactJsonLd: String = """
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ContactPage",
              "url": "https://rezident.info/contact",
              "name": "Contact rezident.info",
              "inLanguage": "ro-RO"
            },
            {
              "@type": "Organization",
              "@id": "https://rezident.info/#org",
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "contactType": "customer support",
                  "email": "salut@rezident.info",
                  "availableLanguage": ["ro"],
                  "areaServed": "RO"
                }
              ]
            }
          ]
        }
    """.trimIndent()

    ApplicationLdJson(contactJsonLd)

    Box(
        sectionPaddingStyle.toModifier()
            .backgroundColor(AppTheme.colors.background)
            .fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        NavHeader()

        Column(
            MaxWidthStyle.toModifier()
                .then(contactTextStyle.toModifier()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                "Contactează-ne",
                AppTheme.typography.title.toModifier()
                    .color(AppTheme.colors.text)
            )

            Spacer(Modifier.height(8.px))

            Div {
                SpanText(
                    "Pentru întrebări, sugestii sau parteneriate, nu ezitați să ne contactați pe ",
                    AppTheme.typography.body1.toModifier()
                        .color(AppTheme.colors.text)
                )

                Link(
                    "https://www.instagram.com/rezident.info",
                    "pagina de Instagram",
                    AppTheme.typography.body1.toModifier()
                        .color(AppTheme.colors.text)
                        .textDecorationLine(TextDecorationLine.Underline)
                )

                SpanText(
                    ", sau în acest formular.",
                    AppTheme.typography.body1.toModifier()
                        .color(AppTheme.colors.text)
                )
            }

            Spacer(Modifier.height(32.px))

            Column(
                contactSectionStyle.toModifier()
                    .backgroundColor(AppTheme.colors.section)
                    .borderRadius(AppTheme.shapes.bigRound)
                    .gap(24.px)
                    .padding(24.px),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                var name by remember { mutableStateOf(LocalStorage.get("contact_name") ?: "") }
                var email by remember { mutableStateOf(LocalStorage.get("contact_email") ?: "") }
                var phoneNumber by remember { mutableStateOf(LocalStorage.get("contact_phoneNumber") ?: "") }
                var message by remember { mutableStateOf(LocalStorage.get("contact_message") ?: "") }
                var isLoading by remember { mutableStateOf(false) }
                var isSuccess by remember { mutableStateOf<Boolean?>(null) }
                val scope = rememberCoroutineScope()

                InputField(
                    inputType = InputType.Text,
                    value = name,
                    placeholder = "Numele tău",
                    isValid = name.isEmpty() || name.isNotBlank(),
                    autoComplete = AutoComplete.name
                ) {
                    name = it
                    LocalStorage.save("contact_name", it)
                }

                InputField(
                    inputType = InputType.Text,
                    value = email,
                    placeholder = "Adresa ta de e-mail",
                    isValid = email.isEmpty() || email.isNotBlank() && email.isValidEmail(),
                    autoComplete = AutoComplete.email
                ) {
                    email = it
                    LocalStorage.save("contact_email", it)
                }

                InputField(
                    inputType = InputType.Tel,
                    value = phoneNumber,
                    placeholder = "Numărul tău de telefon (opțional)",
                    isValid = phoneNumber.isEmpty() || phoneNumber.length == 10,
                    autoComplete = AutoComplete.tel
                ) {
                    phoneNumber = it
                    LocalStorage.save("contact_phoneNumber", it)
                }

                LargeTextArea(
                    value = message,
                    placeholder = "Mesajul tău (minim 5 caractere)",
                    unfocusedBorderColor = AppTheme.colors.text,
                    textColor = AppTheme.colors.text,
                    placeholderStyle = inheritPlaceholderColorStyle,
                    paddingLeftRight = 12.px,
                    paddingTopBottom = 12.px
                ) {
                    message = it
                    LocalStorage.save("contact_message", it)
                }

                if (isSuccess == true) {
                    SpanText(
                        "Mesaj trimis cu succes! Îți mulțumim!",
                        AppTheme.typography.body1Medium.toModifier().color(Colors.Green)
                    )
                } else if (isSuccess == false) {
                    SpanText(
                        "A apărut o eroare. Încearcă din nou.",
                        AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.error)
                    )
                }

                Button(
                    onClick = {
                        scope.launch {
                            isLoading = true
                            isSuccess = null
                            val result = ContactRepository.uploadContactMessage(
                                ContactMessage(
                                    name = name,
                                    email = email,
                                    phoneNumber = phoneNumber.takeIf { it.isNotBlank() },
                                    message = message
                                )
                            )
                            isLoading = false
                            isSuccess = result
                            if (result) {
                                AppTracking.logEvent(AppTracking.EVENT_CONTACT_FORM_SENT)
                                name = ""
                                email = ""
                                phoneNumber = ""
                                message = ""
                                LocalStorage.remove("contact_name")
                                LocalStorage.remove("contact_email")
                                LocalStorage.remove("contact_phoneNumber")
                                LocalStorage.remove("contact_message")
                            }
                        }
                    },
                    buttonStyle.toModifier()
                        .borderRadius(AppTheme.shapes.mediumRound)
                        .setVariable(ButtonVars.BackgroundDefaultColor, AppTheme.colors.accent)
                        .fillMaxWidth(),
                    enabled = !isLoading && name.isNotBlank() && email.isNotBlank() && email.isValidEmail()
                            && message.isNotBlank() && message.length >= 5,
                    size = ButtonSize.LG,
                    colorPalette = AppTheme.colors.accentColorPalette
                ) {
                    if (isLoading) {
                        SpanText(
                            "Se trimite...",
                            AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.onAccent)
                        )
                    } else {
                        SpanText(
                            "Trimite",
                            AppTheme.typography.body1Medium.toModifier().color(AppTheme.colors.onAccent)
                        )
                    }
                }
            }
        }
    }
}

val contactTextStyle = CssStyle {
    base {
        Modifier.alignItems(AlignItems.Start)
    }

    Breakpoint.MD {
        Modifier.alignItems(AlignItems.Center)
    }
}

val contactSectionStyle = CssStyle {
    base {
        Modifier.fillMaxWidth()
    }

    Breakpoint.MD {
        Modifier.fillMaxWidth(60.percent)
    }
}

val sectionPaddingStyle = CssStyle {
    base {
        Modifier.padding(leftRight = 24.px, topBottom = 36.px)
    }

    Breakpoint.MD {
        Modifier.padding(leftRight = 30.px, topBottom = 46.px)
    }

    Breakpoint.LG {
        Modifier.padding(leftRight = 36.px, topBottom = 50.px)
    }

    Breakpoint.XL {
        Modifier.padding(leftRight = 42.px, topBottom = 54.px)
    }
}